/** @typedef {import('@/models/Todo').Todo} Todo */
import { ref } from 'vue'
import { getAll, addTodoToDatabase, updateTodoInDatabase, completedTodoToDatabase, removeTodoToDatabase } from '@/api/todoApi' // API層をインポート
import { useAuthStore } from '@/stores/auth.js';

/**
 * Todoリストの管理と操作を行うコンポーザブル
 * * @returns {{
 * todos: import('vue').Ref<Map<number, Todo>>,
 * isLoadingTodo: import('vue').Ref<boolean>,
 * fetchTodos: () => Promise<void>,
 * addTodo: (todo: Partial<Todo>) => Promise<void>,
 * updateTodo: (updated: Todo) => Promise<void>
 * }}
 */
export function useTodos() {
    const auth = useAuthStore();
    const todos = ref([]);
    const isLoadingTodo = ref(true); //初回読み込み用
    const processingId = ref(null); //追加・削除・更新の通信用

    // 取得
    const fetchTodos = async () => {
        isLoadingTodo.value = true;
        if (!auth.user) {
            return;
        }
        try {
            // API層の関数を呼び出す
            todos.value = await getAll(auth.user.id)
            console.log("読み込み完了");
        } catch (err) {
            console.error(err)
        } finally {
            isLoadingTodo.value = false
        }
    }


    // 追加
    const addTodo = async (todo) => {
        if (!auth.user) {
            return;
        }
        processingId.value = todo.todoId;
        try {
            // API層の関数を呼び出す
            const data = await addTodoToDatabase(auth.user.id, todo);
            if (data) {
                todos.value.set(data.todoId, data);
            };
            console.log("追加完了");
        } catch (err) {
            console.error(err)
        } finally {
            processingId.value = null
        }
    }

    // 更新
    const updateTodo = async (updated) => {
        if (!auth.user) {
            return;
        }
        processingId.value = updated.todoId;
        try {
            const data = await updateTodoInDatabase(auth.user.id, updated);
            console.log(data);
            if (data) {
                todos.value.set(data.todoId, data);
                console.log("更新完了");
            };
        } catch (err) {
            console.error(err)
        } finally {
            processingId.value = null;
        }
    }

    // 完了
    const toggleCompletedTodo = async (todo, checked) => {
        if (!auth.user) {
            return;
        }
        processingId.value = todo.todoId;
        try {
            //完了時
            if (checked) {
                todo.completedAt = new Date().toISOString();
            } else {
                todo.completedAt = null;
            }
            const data = await completedTodoToDatabase(todo);
            if (data) {
                todos.value.set(data.todoId, data);
                console.log("チェックの切り替え完了");
            };
        } catch (err) {
            console.error(err)
        } finally {
            processingId.value = null;
        }
    }

    // 論理削除
    const removeTodo = async (todo, isUndo = false) => {
        if (!auth.user) {
            return;
        }
        const prevState = todo.deletedAt;
        todo.deletedAt = isUndo ? null : new Date().toISOString();
        processingId.value = todo.todoId;
        try {
            const data = await removeTodoToDatabase(todo);
            if (data) {
                todos.value.set(data.todoId, data);
                console.log(isUndo ? "取り消し完了" : "削除完了");
            };
        } catch (err) {
            // 通信失敗時は元の状態に戻す
            todo.deletedAt = prevState;
            console.error("DB更新失敗、ロールバックしました:", err);
        } finally {
            processingId.value = null
        }
    }

    return {
        todos,
        isLoadingTodo,
        processingId,
        fetchTodos,
        addTodo,
        updateTodo,
        toggleCompletedTodo,
        removeTodo
    }
}